import { Link } from '@mui/material'
import { captureException } from '@sentry/react'
import { collection, getDocs, query, Timestamp, where } from 'firebase/firestore'
import moment, { Moment } from 'moment'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { db } from 'src/services/firebase-compat'
import { SubChainData } from 'src/services/openApiV2'
import { pluralize } from 'src/utils/functions/pluralize'
import { useAuth } from './AuthContext'
import { useErrorData } from './ErrorContext'
import { useSubChainContext } from './SubChainContext'

export enum BillingActivePhase {
  PHASE1 = 'phase1',
  PHASE2 = 'phase2',
  EXPIRED = 'expired'
}
export enum BillingClassificationType {
  CHAIN = 'chain',
  FRANCHISE = 'franchise',
  USER = 'user'
}

export const BillingActivePhaseStyles = {
  [BillingActivePhase.PHASE1]: {
    primaryColor: '#673617',
    secondaryColor: '#E28040',
    backgroundColor: '#F7DFB6',
    texts: (activeBillingConfig: BillingConfig) => {
      return {
        first: (
          <>
            Your Loop subscription plan is approaching its due date on <span style={{ fontWeight: 600 }}>{activeBillingConfig.gracePeriod?.date}</span>.
          </>
        ),
        second: (
          <>
            Your account shall be suspended in next{' '}
            <span style={{ fontWeight: 600 }}>
              {activeBillingConfig.gracePeriod?.daysLeft} {pluralize('day', activeBillingConfig.gracePeriod?.daysLeft)}
            </span>
            . Complete payment to continue using Loop modules services.
          </>
        ),
        third: (
          <>
            In case of any queries mail us on{' '}
            <Link
              href="mailto:billing@tryloop.ai"
              style={{
                fontWeight: 600,
                color: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor,
                textDecorationColor: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor
              }}>
              billing@tryloop.ai
            </Link>
            , we will get back to you at earliest.
          </>
        )
      }
    }
  },
  [BillingActivePhase.PHASE2]: {
    primaryColor: '#681923',
    secondaryColor: '#CD3546',
    backgroundColor: '#F1CBD2D9',
    texts: (activeBillingConfig: BillingConfig) => {
      return {
        first: (
          <>
            Your Loop subscription plan is approaching its due date on <span style={{ fontWeight: 600 }}>{activeBillingConfig.gracePeriod?.date}</span>.
          </>
        ),
        second: (
          <>
            Your account shall be suspended in next{' '}
            <span style={{ fontWeight: 600 }}>
              {activeBillingConfig.gracePeriod?.daysLeft} {pluralize('day', activeBillingConfig.gracePeriod?.daysLeft)}
            </span>
            . Complete payment to continue using Loop modules services.
          </>
        ),
        third: (
          <>
            In case of any queries mail us on{' '}
            <Link
              href="mailto:billing@tryloop.ai"
              style={{
                fontWeight: 600,
                color: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor,
                textDecorationColor: BillingActivePhaseStyles[activeBillingConfig?.activePhase]?.primaryColor
              }}>
              billing@tryloop.ai
            </Link>
            , we will get back to you at earliest.
          </>
        )
      }
    }
  }
}

interface GracePeriod {
  date: string
  days: number
  daysLeft: number
}

interface DatabaseBillingConfig {
  isActive: boolean
  paymentLink: string
  classificationType: BillingClassificationType
  paymentAmount: number
  franchise: {
    id: number
    name: string
  }
  chain: {
    id: string
    name: string
  }
  user: {
    email: string
    name: string
  }
  gracePeriods: {
    phase1Days: number
    phase2Days: number
  }
  createdAt: string
  updatedAt: string
}

export interface BillingConfig {
  isActive: boolean
  paymentLink: string
  paymentAmount: number
  gracePeriod: GracePeriod | null
  classificationType: BillingClassificationType
  activePhase: BillingActivePhase
  lastDate: Moment | null
  franchise: {
    id: number
    name: string
  }
  chain: {
    id: string
    name: string
  }
  user: {
    email: string
    name: string
  }
  createdAt: string
  updatedAt: string
}

interface BillingContextInterface {
  activeBillingConfig: BillingConfig | null
  isBillingConfigExpired: boolean
  billingBannerOpen: boolean
  setBillingBannerOpen: React.Dispatch<React.SetStateAction<boolean>>
  billingDialogOpen: boolean
  setBillingDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  billingLoading: boolean
}

export const BillingContext = createContext<BillingContextInterface>({
  activeBillingConfig: null,
  isBillingConfigExpired: false,
  billingBannerOpen: false,
  setBillingBannerOpen: () => {},
  billingDialogOpen: false,
  setBillingDialogOpen: () => {},
  billingLoading: false
})

export const useBillingContext = () => {
  return useContext(BillingContext)
}

interface BillingContextProviderProps {
  children: React.ReactNode
}

// Helper function to convert createdAt to moment
const convertToMoment = (createdAt: string | Timestamp): Moment | null => {
  if (typeof createdAt === 'string') {
    return moment(createdAt)
  } else if (createdAt && typeof createdAt === 'object' && 'seconds' in createdAt) {
    // Handle Firebase timestamp format
    const milliseconds = createdAt.seconds * 1000 + Math.floor(createdAt.nanoseconds / 1000000)
    return moment(milliseconds)
  } else if (createdAt instanceof Date) {
    return moment(createdAt)
  } else {
    // Fallback to null for invalid input
    return null
  }
}

const doesConfigApplyToUser = (
  config: DatabaseBillingConfig,
  currentUserOrg: string,
  currentUserEmail: string,
  currentUserSubChainList: SubChainData[]
): boolean => {
  if (config.classificationType === BillingClassificationType.CHAIN && config.chain.id === currentUserOrg) {
    return true
  } else if (
    config.classificationType === BillingClassificationType.FRANCHISE &&
    currentUserSubChainList?.some((subChain) => subChain?.id === config.franchise?.id)
  ) {
    return true
  } else if (config.classificationType === BillingClassificationType.USER && config.user.email === currentUserEmail) {
    return true
  }
  return false
}

const processDBConfigToClientConfig = (config: DatabaseBillingConfig): BillingConfig => {
  const createdAtMoment = convertToMoment(config.createdAt)
  const phase1Date = createdAtMoment.clone().add(config.gracePeriods.phase1Days, 'days')
  const phase2Date = phase1Date.clone().add(config.gracePeriods.phase2Days, 'days')

  const isPhase1Active = moment().isBefore(phase1Date)
  const isPhase2Active = moment().isBefore(phase2Date) && !isPhase1Active

  return {
    ...config,
    activePhase: isPhase1Active ? BillingActivePhase.PHASE1 : isPhase2Active ? BillingActivePhase.PHASE2 : BillingActivePhase.EXPIRED,
    lastDate: phase2Date,
    gracePeriod: isPhase1Active
      ? {
          date: phase1Date.format('DD MMMM YYYY'),
          days: config.gracePeriods.phase1Days,
          daysLeft: moment(phase1Date).diff(moment(), 'days')
        }
      : isPhase2Active
        ? {
            date: phase2Date.format('DD MMMM YYYY'),
            days: config.gracePeriods.phase2Days,
            daysLeft: moment(phase2Date).diff(moment(), 'days')
          }
        : null
  }
}

export const BillingContextProvider = ({ children }: BillingContextProviderProps) => {
  const { currentUser } = useAuth()
  const { handleError } = useErrorData()
  const { currentUserSubChainList, initialLoading } = useSubChainContext()

  const [activeBillingConfig, setActiveBillingConfig] = useState<BillingConfig | null>(null)
  const [billingBannerOpen, setBillingBannerOpen] = useState(false)
  const [billingDialogOpen, setBillingDialogOpen] = useState(false)
  const [isBillingConfigExpired, setIsBillingConfigExpired] = useState(false)
  const [billingLoading, setBillingLoading] = useState(true)

  // Store raw configs to avoid refetching
  const [rawBillingConfigs, setRawBillingConfigs] = useState<DatabaseBillingConfig[]>([])
  const [configsFetched, setConfigsFetched] = useState(false)

  // Effect 1: Fetch raw billing configs (only when org changes)
  useEffect(() => {
    const fetchBillingConfigs = async () => {
      if (!currentUser?.org) return

      setBillingLoading(true)
      try {
        const res = await getDocs(query(collection(db, 'billing_configs'), where('chain.id', '==', currentUser.org)))

        const configs = res.docs
          .map((doc) => doc.data() as DatabaseBillingConfig)
          .sort((a, b) => convertToMoment(b.createdAt).diff(convertToMoment(a.createdAt)))

        setRawBillingConfigs(configs)
        setConfigsFetched(true)
      } catch (error) {
        handleError(error)
        captureException(error)
        setConfigsFetched(true)
      } finally {
        setBillingLoading(false)
      }
    }

    if (currentUser?.org && !configsFetched) {
      fetchBillingConfigs()
    }
  }, [currentUser?.org, configsFetched])

  // Effect 2: Process configs when user data or configs change
  useEffect(() => {
    const processConfigs = () => {
      if (!configsFetched || !currentUser?.org || initialLoading) {
        setBillingLoading(true)
        return
      }

      let activeBillingConfig = null

      // Find the first active config that applies to the user
      for (const config of rawBillingConfigs) {
        if (config.isActive && doesConfigApplyToUser(config, currentUser.org, currentUser.email, currentUserSubChainList)) {
          activeBillingConfig = config
          break
        }
      }

      if (activeBillingConfig) {
        const clientConfig = processDBConfigToClientConfig(activeBillingConfig)
        setActiveBillingConfig(clientConfig)
        setBillingBannerOpen(clientConfig.activePhase === BillingActivePhase.PHASE1)
        setBillingDialogOpen(clientConfig.activePhase === BillingActivePhase.PHASE2)
        setIsBillingConfigExpired(clientConfig.activePhase === BillingActivePhase.EXPIRED)
      } else {
        // No applicable config found
        setActiveBillingConfig(null)
        setBillingBannerOpen(false)
        setBillingDialogOpen(false)
        setIsBillingConfigExpired(false)
      }

      setBillingLoading(false)
    }

    processConfigs()
  }, [configsFetched, currentUser?.org, currentUser?.email, rawBillingConfigs, initialLoading, currentUserSubChainList])

  const contextValue = useMemo(() => {
    return {
      activeBillingConfig,
      billingBannerOpen,
      setBillingBannerOpen,
      billingDialogOpen,
      setBillingDialogOpen,
      isBillingConfigExpired,
      billingLoading
    }
  }, [activeBillingConfig, billingBannerOpen, billingDialogOpen, setBillingBannerOpen, setBillingDialogOpen, billingLoading])

  return <BillingContext.Provider value={contextValue}>{children}</BillingContext.Provider>
}
